/**
 * pages/admin/CompanySettings.jsx
 * Admin can configure company details used on all invoices.
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Save, Building2, CreditCard, FileText, Percent } from 'lucide-react';
import client from '../../api/client';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import './CompanySettings.css';

const Section = ({ title, icon: Icon, children }) => (
  <div className="cs-section">
    <div className="cs-section-head">
      <Icon size={15} />
      <h4>{title}</h4>
    </div>
    <div className="cs-section-body">{children}</div>
  </div>
);

const CompanySettings = () => {
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await client.get('/company-settings');
      setForm(r.data.settings);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put('/company-settings', form);
      toast.success('Company settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const sf = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!form)   return null;

  return (
    <div className="cs-wrap animate-in">
      <div className="cs-header">
        <div>
          <h2>Company Settings</h2>
          <p className="text-sm text-muted">These details appear on all Proforma and Tax Invoices</p>
        </div>
        <Button icon={Save} loading={saving} onClick={handleSave}>Save All Settings</Button>
      </div>

      {/* Basic Info */}
      <Section title="Company Information" icon={Building2}>
        <div className="cs-grid-2">
          <Input label="Company Name" value={form.companyName || ''} onChange={sf('companyName')} placeholder="Drusshti Services" />
          <Input label="Tagline" value={form.tagline || ''} onChange={sf('tagline')} placeholder="Managing Services the Smarter Way" />
        </div>
        <div className="cs-grid-2">
          <Input label="Email" type="email" value={form.email || ''} onChange={sf('email')} placeholder="info@drusshti.com" />
          <Input label="Telephone" value={form.telephone || ''} onChange={sf('telephone')} placeholder="+91 98765 43210" />
        </div>
        <Input label="Website" value={form.website || ''} onChange={sf('website')} placeholder="www.drusshti.com" />
      </Section>

      {/* Registered Address */}
      <Section title="Registered Address" icon={Building2}>
        <Input label="Address Line 1" value={form.addressLine1 || ''} onChange={sf('addressLine1')} placeholder="Office No, Building Name, Street" />
        <Input label="Address Line 2" value={form.addressLine2 || ''} onChange={sf('addressLine2')} placeholder="Area, Locality" />
        <div className="cs-grid-3">
          <Input label="City"    value={form.city    || ''} onChange={sf('city')}    placeholder="Mumbai" />
          <Input label="State"   value={form.state   || ''} onChange={sf('state')}   placeholder="Maharashtra" />
          <Input label="Pincode" value={form.pincode || ''} onChange={sf('pincode')} placeholder="400001" />
        </div>
      </Section>

      {/* Legal */}
      <Section title="Legal & Registration" icon={FileText}>
        <div className="cs-grid-2">
          <Input label="GST Number (GSTIN)" value={form.gstNumber || ''} onChange={sf('gstNumber')} placeholder="27AABCS1429B1ZB" />
          <Input label="PAN Number" value={form.panNumber || ''} onChange={sf('panNumber')} placeholder="AABCS1429B" />
        </div>
        <div className="cs-grid-2">
          <Input label="Registration Date" type="date" value={form.registrationDate || ''} onChange={sf('registrationDate')} />
          <div className="field">
            <label className="field-label">GST Rate (%)</label>
            <div className="field-control">
              <select className="field-input" value={form.gstRate || 18} onChange={sf('gstRate')}>
                <option value={0}>0% (Exempt)</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18% (Standard)</option>
                <option value={28}>28%</option>
              </select>
            </div>
          </div>
        </div>
      </Section>

      {/* Bank Details */}
      <Section title="Bank Details (shown on invoices)" icon={CreditCard}>
        <div className="cs-grid-2">
          <Input label="Bank Name" value={form.bankName || ''} onChange={sf('bankName')} placeholder="State Bank of India" />
          <Input label="Branch" value={form.bankBranch || ''} onChange={sf('bankBranch')} placeholder="Main Branch, Mumbai" />
        </div>
        <div className="cs-grid-2">
          <Input label="Account Number" value={form.accountNumber || ''} onChange={sf('accountNumber')} placeholder="0123456789" />
          <Input label="IFSC Code" value={form.ifscCode || ''} onChange={sf('ifscCode')} placeholder="SBIN0000123" />
        </div>
        <Input label="UPI ID" value={form.upiId || ''} onChange={sf('upiId')} placeholder="drusshti@sbi" />
      </Section>

      {/* Terms */}
      <Section title="Terms & Conditions" icon={FileText}>
        <div className="cs-grid-terms">
          <div className="field">
            <label className="field-label">Proforma Invoice Terms</label>
            <textarea
              className="field-input cs-textarea"
              value={form.proformaTerms || ''}
              onChange={sf('proformaTerms')}
              placeholder="Enter terms for Proforma Invoice..."
              rows={6}
            />
            <span className="field-hint">Shown on Proforma invoices (before payment)</span>
          </div>
          <div className="field">
            <label className="field-label">Tax Invoice Terms</label>
            <textarea
              className="field-input cs-textarea"
              value={form.taxInvoiceTerms || ''}
              onChange={sf('taxInvoiceTerms')}
              placeholder="Enter terms for Tax Invoice..."
              rows={6}
            />
            <span className="field-hint">Shown on Tax invoices (after payment confirmed)</span>
          </div>
        </div>
      </Section>

      {/* Save */}
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:'0.5rem' }}>
        <Button icon={Save} loading={saving} size="lg" onClick={handleSave}>Save All Settings</Button>
      </div>
    </div>
  );
};

export default CompanySettings;
